import { useEffect, useRef, useState } from 'react'
import type { ComponentType } from 'react'
import {
  Activity,
  AlertTriangle,
  CheckCircle2,
  Database,
  Loader2,
  RefreshCw,
  ServerCog,
  WifiOff,
} from 'lucide-react'
import { API_BASE_URL } from '../api/client'
import { useHealth } from '../hooks/useHealth'
import { formatShortTimestamp } from '../lib/format'
import type { ConnectionState } from '../types/api'

type Tone = 'healthy' | 'warning' | 'critical' | 'pending'

interface ToneStyle {
  dot: string
  icon: string
  headline: string
  badge: string
}

const TONE_STYLES: Record<Tone, ToneStyle> = {
  healthy: {
    dot: 'bg-emerald-500',
    icon: 'text-emerald-600 dark:text-emerald-400',
    headline: 'text-emerald-900 dark:text-emerald-100',
    badge: 'bg-emerald-500/15 text-emerald-700 dark:text-emerald-300',
  },
  warning: {
    dot: 'bg-amber-500',
    icon: 'text-amber-600 dark:text-amber-400',
    headline: 'text-amber-900 dark:text-amber-100',
    badge: 'bg-amber-500/15 text-amber-700 dark:text-amber-300',
  },
  critical: {
    dot: 'bg-salsa-500',
    icon: 'text-salsa-600 dark:text-salsa-400',
    headline: 'text-salsa-900 dark:text-salsa-100',
    badge: 'bg-salsa-500/15 text-salsa-700 dark:text-salsa-300',
  },
  pending: {
    dot: 'bg-slate-400',
    icon: 'text-slate-500 dark:text-slate-400',
    headline: 'text-slate-700 dark:text-slate-200',
    badge: 'bg-slate-500/15 text-slate-600 dark:text-slate-300',
  },
}

/** One dependency line inside the panel — same wording as the old banner, just stacked. */
function DependencyRow({
  label,
  state,
  icon: Icon,
}: {
  label: string
  state: ConnectionState
  icon: ComponentType<{ className?: string }>
}) {
  const style =
    state === 'connected'
      ? 'text-emerald-700 dark:text-emerald-300'
      : state === 'disconnected'
        ? 'text-salsa-700 dark:text-salsa-300'
        : 'text-slate-500 dark:text-slate-400'

  const suffix = state === 'unknown' ? '···' : state === 'connected' ? 'ok' : 'down'

  return (
    <div className="flex items-center justify-between text-[11px]">
      <span className={`inline-flex items-center gap-1.5 font-medium ${style}`}>
        <Icon className="h-3.5 w-3.5" aria-hidden="true" />
        {label}
      </span>
      <span className={`font-mono font-semibold uppercase ${style}`}>{suffix}</span>
    </div>
  )
}

/**
 * Platform status as a top-bar button (GM request, 2026-10-17).
 *
 * The banner used to take a full card at the top of every page for information that is
 * almost always “fine”. It is now a status dot on an icon button — sitting with the
 * notification and theme buttons — which opens the same detail in a small panel:
 * headline, tone, build, the three dependencies and when it was last checked.
 */
export function SystemStatusMenu() {
  const { data, isPending, isError, isFetching, refetch, dataUpdatedAt } = useHealth()
  const [open, setOpen] = useState(false)
  const containerRef = useRef<HTMLDivElement>(null)

  useEffect(() => {
    if (!open) return
    const onPointerDown = (event: MouseEvent) => {
      if (!containerRef.current?.contains(event.target as Node)) setOpen(false)
    }
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') setOpen(false)
    }
    document.addEventListener('mousedown', onPointerDown)
    document.addEventListener('keydown', onKeyDown)
    return () => {
      document.removeEventListener('mousedown', onPointerDown)
      document.removeEventListener('keydown', onKeyDown)
    }
  }, [open])

  let tone: Tone = 'pending'
  let headline = 'Checking platform connectivity…'
  let StatusIcon = Loader2

  if (isError) {
    tone = 'critical'
    headline = 'Store OS API unreachable'
    StatusIcon = WifiOff
  } else if (data?.status === 'healthy') {
    tone = 'healthy'
    headline = 'All systems operational'
    StatusIcon = CheckCircle2
  } else if (data) {
    tone = 'warning'
    headline = 'Degraded — dependency offline'
    StatusIcon = AlertTriangle
  }

  const styles = TONE_STYLES[tone]
  const apiState: ConnectionState = isError ? 'disconnected' : data ? 'connected' : 'unknown'
  const postgresState: ConnectionState = data?.postgres ?? (isError ? 'disconnected' : 'unknown')
  const redisState: ConnectionState = data?.redis ?? (isError ? 'disconnected' : 'unknown')

  const checkedAt = data?.checked_at
    ? formatShortTimestamp(data.checked_at)
    : dataUpdatedAt
      ? formatShortTimestamp(new Date(dataUpdatedAt).toISOString())
      : null

  const toneLabel =
    tone === 'healthy'
      ? 'healthy'
      : tone === 'warning'
        ? 'degraded'
        : tone === 'critical'
          ? 'down'
          : 'checking'

  return (
    <div className="relative" ref={containerRef}>
      <button
        type="button"
        onClick={() => setOpen((value) => !value)}
        aria-expanded={open}
        aria-haspopup="dialog"
        className="icon-button relative"
        aria-label="Platform status"
        title="Platform status"
      >
        <Activity className="h-4 w-4" aria-hidden="true" />
        <span
          className={`absolute -bottom-0.5 -right-0.5 h-2.5 w-2.5 rounded-full ring-2 ring-white dark:ring-ink-950 ${styles.dot}`}
          aria-hidden="true"
        />
      </button>

      {open && (
        <div
          role="dialog"
          aria-label="Platform status"
          className="absolute right-0 top-full z-50 mt-2 w-80 max-w-[85vw] rounded-2xl border border-slate-200 bg-white p-3 shadow-xl dark:border-white/10 dark:bg-ink-900"
        >
          <div className="flex items-start gap-2">
            <StatusIcon
              className={`mt-0.5 h-4 w-4 shrink-0 ${styles.icon} ${isPending ? 'animate-spin' : ''}`}
              aria-hidden="true"
            />
            <div className="min-w-0 space-y-1">
              <div className="flex flex-wrap items-center gap-2">
                <span className={`text-sm font-semibold ${styles.headline}`}>{headline}</span>
                <span className={`badge ${styles.badge}`}>
                  <span className={`h-1.5 w-1.5 rounded-full ${styles.dot}`} />
                  {toneLabel}
                </span>
              </div>
              <p className="text-[11px] text-slate-500 dark:text-slate-400">
                {data
                  ? `${data.app} v${data.version} · ${data.environment} · ${API_BASE_URL}`
                  : `Probing ${API_BASE_URL}`}
              </p>
            </div>
          </div>

          <div className="mt-3 space-y-1 border-t border-slate-100 pt-2 dark:border-white/5">
            <DependencyRow label="api" state={apiState} icon={Activity} />
            <DependencyRow label="postgres" state={postgresState} icon={Database} />
            <DependencyRow label="redis" state={redisState} icon={ServerCog} />
          </div>

          <div className="mt-2 flex items-center justify-between border-t border-slate-100 pt-2 dark:border-white/5">
            <span className="text-[11px] text-slate-500 dark:text-slate-400">
              {checkedAt ? `last check ${checkedAt}` : 'no check yet'}
            </span>
            <button
              type="button"
              onClick={() => void refetch()}
              className="inline-flex items-center gap-1 rounded-lg px-2 py-1 text-[11px] font-semibold text-salsa-600 transition-colors hover:bg-salsa-500/10 dark:text-salsa-400"
            >
              <RefreshCw
                className={`h-3 w-3 ${isFetching ? 'animate-spin' : ''}`}
                aria-hidden="true"
              />
              Re-check
            </button>
          </div>
        </div>
      )}
    </div>
  )
}
